import { Helmet } from "react-helmet-async";
import logoImage from "../assest/images/logo-img.png";
import heroImage from "../assest/images/supplier_landing_hero_trimmed.png";

/**
 * SEO + GEO (Generative Engine Optimization) + AEO (Answer Engine Optimization)
 * for the public supplier landing page.
 *  - SEO  : title, description, canonical, robots, Open Graph, Twitter cards
 *  - GEO  : rich JSON-LD @graph (Organization / WebSite / WebPage / Service) so
 *           generative engines can understand who we are and what we offer
 *  - AEO  : FAQPage structured data so answer engines can surface direct answers
 */

const SITE_NAME = "ToursXplorer Supplier Portal";
const TITLE = "Become a Travel Supplier — Grow Your Business | ToursXplorer";
const DESCRIPTION =
    "ToursXplorer Supplier Portal — list your tours and activities, manage bookings, get paid securely, and reach millions of travelers. Free to join.";
const KEYWORDS =
    "travel supplier portal, become a tour supplier, sell tours online, list activities, tour operator platform, tour booking software, Bokun integration, FareHarbor integration, Pluralo, Rezdy, octo, grow tour business, ToursXplorer suppliers";

const FAQS = [
    {
        q: "How do I become a supplier on ToursXplorer?",
        a: "Create a free supplier account, complete your company profile, add your tours and activities with availability, and start receiving bookings from travelers worldwide.",
    },
    {
        q: "Is it free to join ToursXplorer as a supplier?",
        a: "Yes. Joining is completely free with no setup fees — you can list your products and start growing your business at no upfront cost.",
    },
    {
        q: "Which booking systems does ToursXplorer integrate with?",
        a: "ToursXplorer integrates with leading reservation systems including Bókun, FareHarbor, Pluralo, Rezdy and octo, so you can sync products and availability automatically.",
    },
    {
        q: "How do suppliers get paid?",
        a: "Suppliers are paid securely and on time through our trusted payout system, with clear reporting on bookings and revenue.",
    },
    {
        q: "Can I manage my availability in real time?",
        a: "Yes. Availability syncs in real time across channels, helping you avoid double bookings and keep your calendar accurate.",
    },
];

export default function LandingSeo() {
    const origin =
        (typeof window !== "undefined" && window.location && window.location.origin) ||
        "https://supplier.toursxplorer.com";
    const url = origin + "/";
    const orgId = origin + "/#organization";
    const siteId = origin + "/#website";
    const logoUrl = origin + logoImage;
    const ogImage = origin + heroImage;

    const graph = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "@id": orgId,
                name: "ToursXplorer",
                url: url,
                logo: { "@type": "ImageObject", url: logoUrl },
                description: DESCRIPTION,
                sameAs: [
                    "https://www.facebook.com/toursXplorer",
                    "https://www.instagram.com/toursxplorer/",
                    "https://twitter.com/Tours_xplorer",
                    "https://t.me/toursXplorer",
                ],
            },
            {
                "@type": "WebSite",
                "@id": siteId,
                url: url,
                name: SITE_NAME,
                publisher: { "@id": orgId },
                inLanguage: "en",
            },
            {
                "@type": "WebPage",
                "@id": url + "#webpage",
                url: url,
                name: TITLE,
                description: DESCRIPTION,
                isPartOf: { "@id": siteId },
                about: { "@id": orgId },
                primaryImageOfPage: { "@type": "ImageObject", url: ogImage },
                inLanguage: "en",
            },
            {
                "@type": "Service",
                "@id": url + "#supplier-program",
                name: "ToursXplorer Supplier Program",
                serviceType: "Tour & activity distribution and booking platform for suppliers",
                provider: { "@id": orgId },
                areaServed: "Worldwide",
                description:
                    "A platform for tour and activity suppliers to list products, manage availability, receive bookings and get paid — with global exposure to millions of travelers.",
                offers: {
                    "@type": "Offer",
                    price: "0",
                    priceCurrency: "USD",
                    description: "Free to join — no setup fees.",
                },
            },
            {
                "@type": "BreadcrumbList",
                "@id": url + "#breadcrumb",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Supplier Portal", item: url },
                ],
            },
            {
                "@type": "FAQPage",
                "@id": url + "#faq",
                mainEntity: FAQS.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            },
        ],
    };

    return (
        <Helmet>
            {/* Primary SEO */}
            <title>{TITLE}</title>
            <meta name="description" content={DESCRIPTION} />
            <meta name="keywords" content={KEYWORDS} />
            <meta name="author" content="ToursXplorer" />
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta name="theme-color" content="#E3BD3E" />
            <link rel="canonical" href={url} />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={TITLE} />
            <meta property="og:description" content={DESCRIPTION} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={TITLE} />
            <meta name="twitter:description" content={DESCRIPTION} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:site" content="@Tours_xplorer" />

            {/* GEO + AEO structured data */}
            <script type="application/ld+json">{JSON.stringify(graph)}</script>
        </Helmet>
    );
}
